import {
  getCustomerOrderLineCancelQty,
  getCustomerOrderLineDiscountRate,
  getCustomerOrderLineUnitDiscountAmount,
  getCustomerOrderLineOrderQty,
  getCustomerOrderLineUnitPrice,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import { discountMultiplierToApiRate } from '#/utils/discountRate';
import { pickRoundingMode } from '#/utils/roundingMode';

function toNumberOrNull(value) {
  if (value === '' || value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toNumberOrZero(value) {
  const n = toNumberOrNull(value);
  return n == null ? 0 : n;
}

/** 空白省略（後端當無折扣）。明確的 0 仍送出。 */
function toDiscountRateOrOmit(value) {
  if (value === '' || value == null) {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function createEmptyCustomerOrderLine(overrides = {}) {
  return {
    id: null,
    productId: '',
    salesUnitName: '',
    orderQty: 1,
    cancelQty: 0,
    unitPrice: '',
    discountRate: '',
    discountAmount: '',
    remark: '',
    ...overrides,
  };
}

/** 無 P 號其他產品列（manualLines；RHF／API key 不變）。 */
export function createEmptyCustomerOrderManualLine(overrides = {}) {
  return {
    id: null,
    itemName: '',
    orderQty: 1,
    unitName: '',
    unitPrice: '',
    remark: '',
    cancelQty: 0,
    ...overrides,
  };
}

/** @deprecated 改用 createEmptyCustomerOrderManualLine */
export function createEmptyCustomerOrderCustomLine(overrides = {}) {
  return createEmptyCustomerOrderManualLine({
    itemName: overrides.name ?? overrides.itemName ?? '',
    ...overrides,
    name: undefined,
  });
}

/**
 * 表單值 → create／update body（camelCase；API 層再 keysToSnake）。
 * **不送**衍生 totals、**不送** priceTaxMode／taxRate／roundingAdjustment／roundingScale。
 * Header 送 taxType、必選 roundingMode（UP／DOWN／HALF_UP；勿空值偷送 HALF_UP）。
 * 明細序：`lines[]`／`manualLines[]` 各自陣列順序即序號（不送 lineNo）。
 * Create：`manualLines` 省略 `id`／`cancelQty`；Update：既有列帶 `id`，可送 `cancelQty`。
 * 產品列 `discountRate` 表單值為價格倍率（0.9＝九折），送出轉成後端折抵比例（0.1）；空白省略（後端預設無折扣）。
 * 產品列 `discountAmount` 為每件固定折扣，與 `discountRate` 二擇一（有填每件折扣就不送折數）。手填列不送折扣。
 */
export function buildCustomerOrderWritePayload(values, {
  updatedAt = null,
  isEdit = false,
  originalCurrencyId = null,
} = {}) {
  const lines = (values.lines ?? []).map((line) => {
    const row = {
      productId: line.productId,
      orderQty: Number(line.orderQty),
      cancelQty: toNumberOrZero(line.cancelQty),
      unitPrice: toNumberOrNull(line.unitPrice),
      remark: line.remark || null,
    };
    const discountAmount = toDiscountRateOrOmit(line.discountAmount);
    const discountRate = toDiscountRateOrOmit(line.discountRate);
    if (discountAmount != null) {
      row.discountAmount = discountAmount;
    } else if (discountRate != null) {
      row.discountRate = discountMultiplierToApiRate(discountRate);
    }

    if (line.id) {
      row.id = line.id;
    }

    return row;
  });

  const manualLines = (values.manualLines ?? []).map((line) => {
    const row = {
      itemName: String(line.itemName ?? '').trim(),
      orderQty: Number(line.orderQty),
      unitName: String(line.unitName ?? '').trim(),
      unitPrice: toNumberOrNull(line.unitPrice),
      remark: line.remark || null,
    };

    if (isEdit) {
      if (line.id) {
        row.id = line.id;
      }
      row.cancelQty = toNumberOrZero(line.cancelQty);
    }

    return row;
  });

  const payload = {
    customerOrderDate: values.customerOrderDate || null,
    customerId: values.customerId || null,
    billingAddressId: values.billingAddressId || null,
    shippingAddressId: values.shippingAddressId || null,
    shippingContactId: values.shippingContactId || null,
    currencyId: values.currencyId || null,
    taxType: values.taxType || 'TAXABLE',
    roundingMode: pickRoundingMode(values.roundingMode),
    issueReason: values.issueReason || null,
    remark: values.remark || null,
    lines,
    manualLines,
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  if (isEdit) {
    const nextId = payload.currencyId;
    const previousId = originalCurrencyId || null;
    if (!nextId || nextId === previousId) {
      delete payload.currencyId;
    }
  }

  return payload;
}

/** detail → 表單 defaultValues。 */
export function mapCustomerOrderDetailToFormValues(detail) {
  return {
    customerOrderDate: detail?.customerOrderDate ?? '',
    customerId: detail?.customer?.id ?? detail?.customerId ?? '',
    billingAddressId: detail?.billingAddressId ?? '',
    shippingAddressId: detail?.shippingAddressId ?? '',
    shippingContactId: detail?.shippingContactId
      ?? detail?.customerSnapshot?.shippingContact?.id
      ?? '',
    currencyId: detail?.currencyId ?? '',
    taxType: detail?.taxType ?? 'TAXABLE',
    taxRate: detail?.taxRate ?? 0.05,
    roundingMode: pickRoundingMode(
      detail?.roundingMode ?? detail?.amountSummary?.roundingMode,
    ) ?? '',
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: (detail?.lines ?? []).map((line) => ({
      id: line.id ?? null,
      productId: line.product?.id ?? line.productId ?? '',
      salesUnitName: line.product?.salesUnitName ?? '',
      // Detail→form 扁平：qtySummary 優先，fallback 舊扁平欄
      orderQty: getCustomerOrderLineOrderQty(line) ?? '',
      cancelQty: getCustomerOrderLineCancelQty(line) ?? 0,
      // business input hydrate 自 line.amount；不回寫派生金額
      unitPrice: getCustomerOrderLineUnitPrice(line) ?? '',
      discountRate: getCustomerOrderLineDiscountRate(line) ?? '',
      // 後端沒回折數（以每件固定折扣保存）時才帶每件折扣，避免兩欄同時有值
      discountAmount: getCustomerOrderLineDiscountRate(line) == null
        ? (getCustomerOrderLineUnitDiscountAmount(line) ?? '')
        : '',
      remark: line.remark ?? '',
    })),
    manualLines: (detail?.manualLines ?? []).map((line) => ({
      id: line.id ?? null,
      itemName: line.itemName ?? '',
      orderQty: getCustomerOrderLineOrderQty(line) ?? '',
      unitName: line.unitName ?? '',
      unitPrice: getCustomerOrderLineUnitPrice(line) ?? '',
      remark: line.remark ?? '',
      cancelQty: getCustomerOrderLineCancelQty(line) ?? 0,
    })),
  };
}
